import { activeRows, nextLocalId, type GoaLineDraft } from "src/components/GoaLineEditor/goaLineDraft";

// Everything the user has entered on the creation page so far. Kept as one plain object so it
// can be saved as a draft as-is later (PLAN.md story 9). Steps add their fields here.
export interface InvoiceDraftData {
    /** Patient the invoice is for — the recipient (who pays) comes from the patient's record. */
    patientId: string | null;
    /** Staff only, in a multi-doctor praxis: the doctor the invoice is for (useInvoiceOwnerChoice).
     *  Doctors/admins always own their own invoices, so this stays empty for them. */
    ownerPick: string;
    /** Behandlungstage, yyyy-mm-dd, sorted and unique — services are entered per day (step 3). */
    treatmentDates: string[];
    /** Free text, in the order entered (shown on the invoice in that order). */
    diagnoses: string[];
    /** GOÄ services per treatment day (yyyy-mm-dd → rows). A day removed in step 2 keeps its rows
     *  here (it may come back) — only the days in `treatmentDates` count. */
    services: Record<string, GoaLineDraft[]>;
}

/** Today as yyyy-mm-dd in the browser's own time zone (not UTC — a late-evening entry in Germany
 *  must not turn into tomorrow or yesterday). */
export const todayIso = (now = new Date()) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** Worth saving as a draft — something was actually entered, not just the page opened. */
export const hasDraftContent = (draft: InvoiceDraftData) =>
    !!draft.patientId || draft.diagnoses.length > 0 || Object.values(draft.services).some((rows) => rows.length > 0);

/**
 * A saved draft back into page state. Fills fields missing from older drafts with today's defaults,
 * and gives every service line and Auslage a fresh local id: those ids come from a counter that
 * restarts with every page load, so restored ids would collide with lines added afterwards.
 */
export function restoreDraft(saved: Partial<InvoiceDraftData> | null | undefined): InvoiceDraftData {
    const base = emptyDraft();
    const services: Record<string, GoaLineDraft[]> = {};
    for (const [day, rows] of Object.entries(saved?.services ?? {})) {
        services[day] = (rows ?? []).map((row) => ({
            ...row,
            _localId: nextLocalId(),
            materialLines: (row.materialLines ?? []).map((ml) => ({ ...ml, _localId: nextLocalId() })),
        }));
    }
    return {
        patientId: saved?.patientId ?? base.patientId,
        ownerPick: saved?.ownerPick ?? base.ownerPick,
        treatmentDates: saved?.treatmentDates?.length ? saved.treatmentDates : base.treatmentDates,
        diagnoses: saved?.diagnoses ?? base.diagnoses,
        services,
    };
}

/** Body for POST /invoices/manual (backend services/manualInvoice.ts). Only the treatment days
 *  still selected count; `doctorId` is sent only when the user actually picks an owner (staff). */
export function toManualInvoicePayload(draft: InvoiceDraftData, sendOwnerPick: boolean, draftId?: string | null) {
    return {
        ...(draftId ? { draftId } : {}),
        patientId: draft.patientId,
        ...(sendOwnerPick && draft.ownerPick ? { doctorId: draft.ownerPick } : {}),
        diagnoses: draft.diagnoses,
        services: draft.treatmentDates.flatMap((date) =>
            activeRows(draft.services[date] ?? []).map((row) => ({
                date,
                code: row.code.trim(),
                description: row.description.trim(),
                quantity: row.quantity,
                price: row.price,
                factor: row.factor,
                justification: row.justification.trim() || undefined,
                materialLines: row.materialLines.map(({ kind, label, cost }) => ({ kind, label: label.trim(), cost })),
            })),
        ),
    };
}

export const emptyDraft = (): InvoiceDraftData => ({
    patientId: null,
    ownerPick: "",
    treatmentDates: [todayIso()],
    diagnoses: [],
    services: {},
});
